/**
 * CartItems Class
 * Direct conversion from TypeScript master cart/cartItems.ts
 */
// @ts-nocheck
import CartManager from './cart-manager.js';
import { formatMoney, imgURL } from './shopify-utils.js';

class CartItems {
  constructor(items = [], parent = document.body) {
    this.mediaDimension = '96x96';
    this.cartManager = CartManager.getInstance();
    this.disallowedProps = ['Media', 'Certificate'];
    this.items = items;
    this.parent = parent;
    this.noProductTemplateElement = document.getElementById('empty-cart-template');
    this.update(items);
  }

  update(items) {
    this.items = items;
    try {
      const filteredItems = items.filter(item => !item.hidden);
      const itemsWithoutCloverly = filteredItems.filter(item => item.product_id != 7704109711498);

      if (itemsWithoutCloverly.length > 0) {
        this.renderItems(itemsWithoutCloverly);
      } else {
        this.renderEmptyCart();
      }
    } catch (err) {
      console.error('[CartItems.update] render failed', err, { items });
    }
  }

  renderItems(items) {
    // Clear existing content
    this.parent.innerHTML = '';

    // Render each item
    items.forEach(item => {
      const itemElement = this.createItemElement(item);
      this.parent.appendChild(itemElement);
    });
  }

  renderEmptyCart() {
    if (this.noProductTemplateElement) {
      this.parent.innerHTML = '';
      const emptyContent = this.noProductTemplateElement.content.cloneNode(true);
      this.parent.appendChild(emptyContent);
    }
  }

  createItemElement(item) {
    const div = document.createElement('div');

    // Check if item should be hidden
    const isProductHidden = item.metafields?.seo?.hidden === 1;
    const hideQty = !!item.properties?.['_rugBundleId'];

    // Build classes
    let classes = 'slideoutItemListing';
    if (isProductHidden) classes += ' slideoutItemListing--hidden';
    if (hideQty) classes += ' slideoutItemListing--hide-qty';

    div.className = classes;
    div.dataset.itemKey = item.key; // Store key for reference

    // Create content wrapper
    const content = document.createElement('div');
    content.className = 'slideoutItemListing__content';

    // Image
    const imageEl = this.createImageElement(item);
    if (imageEl) content.appendChild(imageEl);

    // Details
    const details = this.createDetailsElement(item);
    content.appendChild(details);

    // Actions
    const actions = this.createActionsElement(item);
    content.appendChild(actions);

    div.appendChild(content);
    return div;
  }

  createImageElement(item) {
    const mediaGalleryImage = item.media_gallery_image || '';
    const hasImg = typeof item.image === 'string' && item.image.length > 0;
    const hasMedia = typeof mediaGalleryImage === 'string' && mediaGalleryImage.length > 0;
    const isNoImage = hasImg && item.image.indexOf('no-image') !== -1;
    const isImageEmpty = (!hasImg && !hasMedia) || isNoImage;

    if (isImageEmpty) {
      const emptyDiv = document.createElement('div');
      emptyDiv.className = 'slideoutItemListing__image slideoutItemListing__image--empty';
      emptyDiv.setAttribute('aria-hidden', 'true');
      return emptyDiv;
    }

    const src = hasMedia ? mediaGalleryImage : (hasImg ? item.image : null);
    if (!src) return null;

    // Convert image URL to 96x96 size
    const imgUrl = this.getImageUrl(src, this.mediaDimension);

    const imageContainer = document.createElement('div');
    imageContainer.className = 'slideoutItemListing__image';

    const isProductHidden = item.metafields?.seo?.hidden === 1;
    const href = isProductHidden ? undefined : item.url;

    const img = document.createElement('img');
    img.src = imgUrl;
    img.alt = item.title || '';
    img.className = 'slideoutItemListing__img';

    if (href) {
      const link = document.createElement('a');
      link.href = href;
      link.appendChild(img);
      imageContainer.appendChild(link);
    } else {
      imageContainer.appendChild(img);
    }

    return imageContainer;
  }

  createDetailsElement(item) {
    const details = document.createElement('div');
    details.className = 'slideoutItemListing__details';

    // Badges
    if (item.badges && item.badges.length > 0) {
      const badgesDiv = document.createElement('div');
      badgesDiv.className = 'product-badges slideoutItemListing__badges';
      item.badges.forEach(badge => {
        const badgeSpan = document.createElement('span');
        badgeSpan.className = 'product-badge product-badge--promo slideoutItemListing__badge';
        badgeSpan.style.setProperty('--color-text', badge.color || '');
        badgeSpan.style.setProperty('--color-bg', badge.background || '');
        badgeSpan.textContent = badge.text || '';
        badgesDiv.appendChild(badgeSpan);
      });
      details.appendChild(badgesDiv);
    }

    // Title
    const title = document.createElement('h4');
    title.className = 'slideoutItemListing__name';
    const isProductHidden = item.metafields?.seo?.hidden === 1;
    const itemTitle = item.metafields?.custom?.short_title || item.product_title || '';
    const href = isProductHidden ? undefined : item.url;

    if (href) {
      const link = document.createElement('a');
      link.href = href;
      link.textContent = itemTitle;
      title.appendChild(link);
    } else {
      title.textContent = itemTitle;
    }
    details.appendChild(title);

    // Price
    const priceEl = this.createPriceElement(item);
    details.appendChild(priceEl);

    if (item.free_shipping) {
      const infoSpan = document.createElement('span');
      infoSpan.style.display = 'block';
      infoSpan.style.fontSize = '12px';
      infoSpan.style.color = '#FE5A86';
      infoSpan.style.marginTop = '4px';
      infoSpan.style.marginBottom = '10px';
      infoSpan.textContent = 'Free Shipping';
      details.appendChild(infoSpan);
    }

    // Variant info
    if (!item.product_has_only_default_variant) {
      const variantInfo = this.createVariantInfoElement(item);
      if (variantInfo) {
        if (item.free_shipping) {
          const variantUl = variantInfo.querySelector('.slideoutItemListing__variantInfo');
          if (variantUl) variantUl.style.margin = '0';
        }
        details.appendChild(variantInfo);
      }
    }

    return details;
  }

  createPriceElement(item) {
    const priceSpan = document.createElement('span');
    priceSpan.className = 'price-value slideoutItemListing__value';

    // Calculate prices
    let customPrice = +item.properties?.['_rugBundlePrice'] || 0;
    const addOnPrice = +item.properties?.['_add_on_price'] || 0;
    const addOnMatchQuantity = item.properties?.['_add_on_match_qty'];
    let totalAddOnPrice = addOnPrice;

    if (addOnMatchQuantity === 'true') {
      totalAddOnPrice = addOnPrice * item.quantity;
    }

    if (addOnPrice) {
      customPrice = item.final_line_price - totalAddOnPrice;
    }

    const linePrice = customPrice || item.final_line_price || 0;
    const hasVariant = !!item.variant;
    const compareAt = hasVariant ? (item.variant.compare_at_price ?? 0) : 0;
    const vPrice = hasVariant ? (item.variant.price ?? 0) : 0;
    const showCompare = (item.original_price !== item.final_price) || (compareAt > vPrice);

    if (showCompare) {
      const comparePrice = compareAt > vPrice
        ? (compareAt * item.quantity) - totalAddOnPrice
        : (item.original_line_price - totalAddOnPrice);

      const compareEl = document.createElement('s');
      compareEl.className = 'price-value__compare';
      compareEl.title = 'Regular Price';
      compareEl.textContent = this.formatMoneyValue(comparePrice);
      priceSpan.appendChild(compareEl);
    }

    const currentEl = document.createElement('span');
    currentEl.className = 'price-value__current';
    currentEl.textContent = this.formatMoneyValue(linePrice);
    priceSpan.appendChild(currentEl);

    return priceSpan;
  }

  createVariantInfoElement(item) {
    const description = document.createElement('div');
    description.className = 'slideoutItemListing__description';

    const ul = document.createElement('ul');
    ul.className = 'slideoutItemListing__variantInfo';

    // Options (Color, Size, etc.)
    if (item.options_with_values && item.options_with_values.length > 0) {
      item.options_with_values.forEach(option => {
        const li = document.createElement('li');
        li.className = 'slideoutItemListing__item';
        li.textContent = `${option.name}: ${option.value}`;
        ul.appendChild(li);
      });
    }

    // Properties (custom properties like Dimension, Shape, etc.)
    if (item.properties) {
      Object.entries(item.properties).forEach(([key, value]) => {
        if (key.startsWith('_') || this.disallowedProps.includes(key)) {
          return; // Skip hidden properties
        }
        const li = document.createElement('li');
        li.className = 'slideoutItemListing__item';
        li.textContent = `${key}: ${value}`;
        ul.appendChild(li);
      });
    }

    // Add-on product info
    const addOnPrice = +item.properties?.['_add_on_price'] || 0;
    const addOnMatchQuantity = item.properties?.['_add_on_match_qty'];
    const addOnProductTitle = item.properties?.['_add_on_title'];
    let totalAddOnPrice = addOnPrice;

    if (addOnMatchQuantity === 'true') {
      totalAddOnPrice = addOnPrice * item.quantity;
    }

    if (totalAddOnPrice && addOnProductTitle) {
      const li = document.createElement('li');
      li.className = 'slideoutItemListing__item';
      const titleSpan = document.createElement('span');
      titleSpan.textContent = `+ ${addOnProductTitle}`;
      const priceSpan = document.createElement('span');
      priceSpan.className = 'price-value__current';
      priceSpan.textContent = this.formatMoneyValue(totalAddOnPrice);
      li.appendChild(titleSpan);
      li.appendChild(priceSpan);
      ul.appendChild(li);
    }

    if (ul.children.length > 0) {
      description.appendChild(ul);
      return description;
    }

    return null;
  }

  createActionsElement(item) {
    const actions = document.createElement('div');
    actions.className = 'slideoutItemListing__actions';

    // Quantity selector
    const quantityDiv = document.createElement('div');
    quantityDiv.className = 'slideoutItemListing__quantity';

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'slideoutItemListing__quantityButton slideoutItemListing__quantityButton--remove';
    removeBtn.setAttribute('aria-label', 'Decrease quantity');
    removeBtn.disabled = item.quantity === 1;
    removeBtn.addEventListener('click', () => this.removeItemQty(item));
    quantityDiv.appendChild(removeBtn);

    const quantityValue = document.createElement('span');
    quantityValue.className = 'slideoutItemListing__quantityValue';
    quantityValue.textContent = item.quantity || 1;
    quantityDiv.appendChild(quantityValue);

    const isSwatch = (item.product_type || '').toLowerCase().includes('swatch');
    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'slideoutItemListing__quantityButton slideoutItemListing__quantityButton--add';
    addBtn.setAttribute('aria-label', 'Increase quantity');
    addBtn.disabled = isSwatch;
    addBtn.addEventListener('click', () => this.addItemQty(item));
    quantityDiv.appendChild(addBtn);

    actions.appendChild(quantityDiv);

    // Remove link
    const actionLinks = document.createElement('div');
    actionLinks.className = 'slideoutItemListing__actionLinks';

    const removeLink = document.createElement('button');
    removeLink.type = 'button';
    removeLink.className = 'slideoutItemListing__actionLink slideoutItemListing__actionLink--remove';
    removeLink.textContent = 'Remove';
    removeLink.addEventListener('click', () => this.removeItem(item));
    actionLinks.appendChild(removeLink);

    actions.appendChild(actionLinks);

    return actions;
  }

  addItemQty(item) {
    const { key, quantity } = item;
    this.updateQuantity(key, quantity + 1);
  }

  removeItemQty(item) {
    const { key, quantity } = item;
    this.updateQuantity(key, Math.max(0, quantity - 1));
  }

  updateQuantity(key, qty) {
    // Direct conversion from TypeScript line 216-218
    this.disableActions();
    this.cartManager.change(key, qty).then(() => {
      this.enableActions();
      // Dispatch cart update event
      const event = new CustomEvent('cart:update', {
        detail: {
          data: {
            cart: this.cartManager.getState()
          }
        }
      });
      document.dispatchEvent(event);
    }).catch(() => {
      this.enableActions();
    });
  }

  removeItem(item) {
    // Direct conversion from TypeScript line 221-232
    const { key, properties } = item;

    console.log('[CartItems.removeItem] Item:', item);
    console.log('[CartItems.removeItem] Key:', key);

    if (properties?.['_rugBundleId']) {
      const bundleId = properties['_rugBundleId'];
      const bundled = this.items
        .filter(i => i.properties?.['_rugBundleId'] === bundleId)
        .map((i) => {
          console.log('[CartItems.removeItem] Bundle item key:', i.key, 'id:', i.id);
          return { quantity: 0, id: i.key || i.id };
        });

      console.log('[CartItems.removeItem] Bundled items to remove:', bundled);

      this.cartManager.changeMany(bundled).then(() => {
        const event = new CustomEvent('cart:update', {
          detail: {
            data: {
              cart: this.cartManager.getState()
            }
          }
        });
        document.dispatchEvent(event);
      }).catch(err => {
        console.error('[CartItems.removeItem] Error:', err);
        this.enableActions();
      });
    } else {
      console.log('[CartItems.removeItem] Single item, calling updateQuantity with key:', key);
      this.updateQuantity(key, 0);
    }
  }

  enableActions() {
    // Direct conversion from TypeScript line 234-239
    const actions = document.querySelectorAll('.cartItem .sign');
    for (let i = 0; i < actions.length; i++) {
      actions[i].disabled = false;
    }
  }

  disableActions() {
    // Direct conversion from TypeScript line 241-245
    const actions = document.querySelectorAll('.cartItem .sign');
    for (let i = 0; i < actions.length; i++) {
      actions[i].disabled = true;
    }
  }

  getImageUrl(src, dimension) {
    return imgURL(src, dimension);
  }

  formatMoneyValue(cents) {
    return formatMoney(cents);
  }
}

export default CartItems;

